//class
import PadEditor from '../PadEditor';
// utils
import _ from 'lodash';
import { codeGeneretor } from 'app/utils';
// consts
import { TYPE } from 'app/config/prescription_content';
// form
import { prescriptionTypeInitFormFn } from 'app/components/modals/pad-editor/prescriptionModal/prescriptionForms/prescriptionTypeForm/utils/prescriptionTypeInitFormFn';
import { prescriptionMedicamentInitFormFn } from 'app/components/modals/pad-editor/prescriptionModal/prescriptionForms/prescriptionMedicamentForm/utils/prescriptionMedicamentInitFormFn';
// classes
import { PadPrescriptionType } from './PadPrescriptionType';

/**
 * represents an instance of PadPrescriptionMedicament
 * @class PadPrescriptionMedicament
 * @extends PadEditor
 */
export default class PadPrescriptionMedicament extends PadEditor {
  prepare(currentPadPrescriptionMedicament = [], previousPadPrescriptionMedicament = []) {
    const prepareAuxFn = padPrescription => {
      const id = padPrescription.id;
      const code = padPrescription.code || codeGeneretor();
      const type = TYPE.SUSTEINED;
      const status = padPrescription.status;
      const padPrescriptionSuggestSched = padPrescription.padPrescriptionSuggestionScheduling;

      padPrescription.suggestScheduling = _.isEmpty(padPrescriptionSuggestSched) ? 0 : 1;

      const prescriptionTypeForm = prescriptionTypeInitFormFn(padPrescription);
      const medicamentForm = prescriptionMedicamentInitFormFn(padPrescription);

      return {
        id,
        code,
        type,
        status,
        prescriptionTypeForm,
        medicamentForm
      };
    };

    const preparedMedicaments = currentPadPrescriptionMedicament.map(prepareAuxFn);
    const prePreparedMedicaments = previousPadPrescriptionMedicament.map(prepareAuxFn);

    this._currentData = _.cloneDeep(preparedMedicaments);
    this._loadedData = _.cloneDeep(prePreparedMedicaments);
    this._screenData = _.cloneDeep(preparedMedicaments);

    return this;
  }

  /**
   * insert a new padPrescriptionMedicament.
   * @param {object} padPrescriptionMedicament - new padPrescriptionMedicament.
   * @returns {object} the state
   */
  add(padPrescriptionMedicament) {
    const currentData = _.cloneDeep(this._currentData);
    const screenData = _.cloneDeep(this._screenData);
    const loadedData = _.cloneDeep(this._loadedData);
    const padPrescription = _.cloneDeep(padPrescriptionMedicament);

    const padPrescriptionType = new PadPrescriptionType({ screenData, loadedData, currentData });
    const type = padPrescriptionType.getTypeOnAdd();

    padPrescription.id = null;
    padPrescription.type = type;
    padPrescription.code = codeGeneretor();

    this._screenData.push(padPrescription);

    return this;
  }

  /**
   * update a padPrescriptionMedicament.
   * @param {string} code - padPrescriptionMedicament`s code to be edited.
   * @param {object} padPrescriptionMedicament - padPrescriptionMedicament to be edited.
   * @returns {object} return the state
   */
  edit(code, padPrescriptionMedicament = {}) {
    const screenData = _.cloneDeep(this._screenData);
    const loadedData = _.cloneDeep(this._loadedData);
    const padPrescription = _.cloneDeep(padPrescriptionMedicament);

    // find index by code
    const indexScreenToEdit = [...this._screenData].findIndex(item => item.code === code);

    // replace item by that to been editing
    Object.entries(padPrescriptionMedicament).forEach(([key, content]) => {
      this._screenData[indexScreenToEdit][key] = content;
    });

    const padPrescriptionType = new PadPrescriptionType({ screenData, loadedData });
    const type = padPrescriptionType.getTypeOnEdit(padPrescription);

    this._screenData[indexScreenToEdit].type = type;

    return this;
  }

  /**
   * remove a element.
   * @param {string} code - element`s code to remove.
   * @returns {object} return the state
   */
  remove(code) {
    const newScreen = [...this._screenData].filter(currentElement => currentElement.code !== code);

    this._screenData = newScreen;

    return this;
  }

  /**
   * suspend a pad prescription padPrescriptionMedicament.
   * @param {string} code - element`s code to suspend.
   * @returns {object} return the state
   */
  suspend(code) {
    const currentData = _.cloneDeep(this._currentData);
    const screenData = _.cloneDeep(this._screenData);
    const loadedData = _.cloneDeep(this._loadedData);

    const padPrescriptionType = new PadPrescriptionType({ screenData, loadedData, currentData });

    const newScreen = [...this._screenData].map(cPadPrescriptionMedicament => {
      // Nada é feito em pads prescrições que não corresponde ao que está sendo suspenso
      if (cPadPrescriptionMedicament.code !== code) {
        return {
          ...cPadPrescriptionMedicament
        };
      }

      // Nada deve ser feito se o pad prescrição já for suspenso. Esse reducer
      // nem deve ser acionado nesses caso, isso se trata de uma validação
      if (cPadPrescriptionMedicament.type === TYPE.SUSPENDED) {
        return {
          ...cPadPrescriptionMedicament
        };
      }

      const type = padPrescriptionType.getTypeOnSuspend(cPadPrescriptionMedicament);

      return {
        ...cPadPrescriptionMedicament,
        type
      };
    });

    this._screenData = newScreen;

    return this;
  }

  /**
   * unsuspend a pad prescription padPrescriptionMedicament.
   * @param {string} code - element`s code to suspend.
   * @returns {object} return the state
   */
  unsuspend(code, prescriptionForm) {
    const currentData = _.cloneDeep(this._currentData);
    const screenData = _.cloneDeep(this._screenData);
    const loadedData = _.cloneDeep(this._loadedData);
    const padPrescription = _.cloneDeep(prescriptionForm);

    const params = { screenData, loadedData, currentData };
    const padPrescriptionType = new PadPrescriptionType(params);

    const newScreen = [...this._screenData].map(cPadPrescriptionMedicament => {
      if (cPadPrescriptionMedicament.code !== code) {
        return {
          ...cPadPrescriptionMedicament
        };
      }

      const padPrescriptionMedicament =
        padPrescriptionType.findPadPrescriptionOnUnsuspend(padPrescription);

      return {
        ...padPrescriptionMedicament
      };
    });

    this._screenData = newScreen;

    return this;
  }

  changeStatus(code) {
    this._screenData = [...this._screenData].map(padPrescriptionMedicament => {
      if (padPrescriptionMedicament.code === code) {
        const status = padPrescriptionMedicament.status === 1 ? 0 : 1;

        return { ...padPrescriptionMedicament, status };
      }

      return { ...padPrescriptionMedicament };
    });

    return this;
  }

  replacesAllPrescriptors(prescriptionForm) {
    const { /* prescriptionTypeForm, */ medicamentForm } = prescriptionForm;
    const { formAddOn } = medicamentForm;
    const { professional } = formAddOn;

    const nScreen = [...this._screenData].map(prescription => {
      return {
        ...prescription,
        medicamentForm: {
          ...prescription.medicamentForm,
          alertMessage: {},

          professionalId: professional.id,
          responsibleForPrescription: {
            id: professional.id,
            name: professional.name,
            profession: professional.profession
          },
          formAddOn: {
            ...prescription.medicamentForm.formAddOn,
            professional: {
              ...professional
            }
          }
        }
      };
    });

    this._screenData = nScreen;
    this._state.data.screen = nScreen;

    return this;
  }
}
